import { useCallback, useState } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Label, ListGroup, ListGroupItem, Badge, Row, Col } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import { toast } from "react-toastify";
import AsyncReactSelect from "src/components/AsyncReactSelect";
import TaskControlsService from "src/modules/data-protection/apis/TaskControlsService";
import { useMutation } from "@tanstack/react-query";

const TaskControls = ({
    t,
    taskId,
    isReadOnly = false,
    defaultValues = []
}) => {
    const [expandedList, setExpandedList] = useState(false);
    const [selectedControls, setSelectedControls] = useState({
        isLoading: false,
        items: defaultValues.map((item) => {
            return {
                value: item.id,
                label: `${item.control_number}. ${t(item.title)}`,
            }
        })
    });

    const taskControlsService = TaskControlsService.getInstance();

    const fetchControlsOptions = useCallback(async (params) => {
        const response = await taskControlsService.fetchControls(params);

        const options = (response || []).map((control) => {
            return {
                value: control.id,
                label: `${control.control_number}. ${control.title}`
            }
        });

        return {
            options: options,
            pageIndex: 1,
            pageSize: 100,
            totalCount: 100
        }
    }, []);

    const {
        mutate: handleAddControl
    } = useMutation({
        mutationFn: async (payload) => {
            return await taskControlsService.addControl(taskId, {
                control: payload.value.toString()
            });
        },
        onSuccess: (_response, payload) => {
            toast(t("The selected control was successfully added."), {
                type: "success"
            });

            setSelectedControls((currentState) => {
                return {
                    ...currentState,
                    items: [
                        ...currentState.items,
                        {
                            ...payload
                        }
                    ]
                }
            });
        },
        onError: () => {
            toast(t("An error occurred while adding the control."), {
                type: "error",
            });
        }
    });

    const {
        mutate: handleRemoveControl
    } = useMutation({
        mutationFn: async (payload) => {
            return await taskControlsService.removeControl(taskId, {
                control: payload.value.toString()
            });
        },
        onSuccess: (_response, payload) => {
            toast(t("The control was successfully removed."), {
                type: "success"
            });

            setSelectedControls((currentState) => {
                return {
                    ...currentState,
                    items: currentState.items.filter((c) => c.value !== payload.value)
                }
            });
        },
        onError: () => {
            toast(t("Failed to remove the control. Please try again."), {
                type: "error"
            });
        }
    });

    return (
        <Row className='mb-5'>
            <Col sm="12">
                <div>
                    <Label className="form-label text-dark d-flex justify-content-between">
                        {t("Controls")}:
                        <span
                            onClick={() => setExpandedList(!expandedList)}
                            className="expand-btn btn-primary d-inline-block ml-auto mr-0 p-1"
                            style={{
                                borderRadius: '0.25rem',
                                lineHeight: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            {expandedList ? t("Collapse list") : t("Expand list")}
                        </span>
                    </Label>

                    <div className="d-flex flex-column items-container">
                        <div
                            style={{
                                border: '1px solid #dadada',
                                padding: '10px',
                                borderRadius: '6px'
                            }}      
                        >   
                            {selectedControls.items.length > 0 ? (
                                <ListGroup
                                    type="inline"
                                    style={{
                                        maxHeight: expandedList ? "171px" : "51px",
                                        overflowY: 'scroll',
                                        transition: 'all .3s ease'
                                    }}
                                >
                                    {selectedControls.items.map((control, index) => {
                                        return (
                                            <ListGroupItem key={index}>
                                                {control.label}
                                                {!isReadOnly && (
                                                    <Badge
                                                        className="delete-btn text-dark pt-2"
                                                        style={{
                                                            float: 'right',
                                                            cursor: 'pointer'
                                                        }}
                                                        color="light"
                                                        onClick={() => {
                                                            handleRemoveControl(control);
                                                        }}
                                                    >
                                                        X
                                                    </Badge>
                                                )}
                                            </ListGroupItem>
                                        )
                                    })}   
                                </ListGroup>
                            ) : (
                                <p
                                    className="empty-list-message"
                                    style={{
                                        maxHeight: expandedList ? "163px" : "43px",
                                    }}
                                >
                                    {t(EMPTY_LIST)}
                                </p>
                            )}

                            <Row className="mt-2">
                                <Col sm='12'>
                                    <AsyncReactSelect 
                                        loadOptions={fetchControlsOptions}
                                        settings={{
                                            queryKey: 'title',
                                            loadOptionsErrorMessage: 'Faild to fetch'
                                        }}
                                        value={[]}
                                        onChange={(e) => {
                                            handleAddControl(e)
                                        }}
                                        placeholder={t("Select a control")}
                                        isDisabled={isReadOnly}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>
            </Col>
        </Row>
    )
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
  
    return {
        token,
        user
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps
    )(
        TaskControls
    )
);